import { SceneInstrumentation } from '@babylonjs/core/Instrumentation/sceneInstrumentation';
import type { Game } from '../core/Game';
import type { DebugLayer } from '../config/debug.config';
import { aabb } from '../physics/Aabb';
import { ColliderDebugRenderer } from './ColliderDebugRenderer';
import { DebugManager } from './DebugManager';
import { DebugPanel } from './DebugPanel';
import { SocketGizmoPresenter } from './SocketGizmoPresenter';

/** Range around the player in which colliders are drawn (meters). */
const COLLIDER_DRAW_RANGE = { behind: 10, ahead: 60 } as const;
/** Ceilings are solid upwards – the debug view shows their underside as a slab this thick (meters). */
const TERRAIN_CEILING_SLAB = 0.25;

/**
 * Wires all debug tooling to a running game. Created in dev builds and in
 * production when the URL contains ?debug=1.
 */
export class DebugController {
  readonly manager: DebugManager;
  private readonly panel: DebugPanel;
  private readonly colliders: ColliderDebugRenderer;
  private instrumentation: SceneInstrumentation | null = null;
  private statsTimer = 0;
  private impressions = 0;
  private readonly box = aabb();

  constructor(
    private readonly game: Game,
    uiRoot: HTMLElement,
    startEnabled: boolean,
  ) {
    const config = game.config.debug;
    this.manager = new DebugManager(config, startEnabled);
    new SocketGizmoPresenter(game.scene, game.trackView, this.manager, config, game.config.level.junctions, game.events);
    this.colliders = new ColliderDebugRenderer(game.scene, game.track, config);
    this.panel = new DebugPanel(uiRoot, this.manager, {
      onGodMode: (enabled) => (game.director.godMode = enabled),
      onAddLife: () => game.score.addExtraLife(),
    });
    game.events.on('ad:impression', () => this.impressions++);
    this.manager.onChange((m) => this.syncInstrumentation(m.enabled));
    this.syncInstrumentation(this.manager.enabled);
  }

  toggle(): void {
    this.manager.toggle();
  }

  isVisible(layer: DebugLayer): boolean {
    return this.manager.isVisible(layer);
  }

  render(dt: number): void {
    this.drawColliders();
    if (!this.manager.enabled) return;
    this.statsTimer += dt;
    if (this.statsTimer < this.game.config.debug.statsUpdateInterval) return;
    this.statsTimer = 0;
    this.panel.update(this.collectStats());
  }

  private drawColliders(): void {
    this.colliders.begin();
    if (this.manager.isVisible('colliders')) {
      const { player, obstacles, collectibles } = this.game;
      const d = player.current.distance;
      const min = d - COLLIDER_DRAW_RANGE.behind;
      const max = d + COLLIDER_DRAW_RANGE.ahead;
      this.colliders.draw(player.colliderOf('current', this.box), 'player');
      for (const obstacle of obstacles.query(min, max)) {
        for (const collider of obstacle.trackColliders) this.colliders.draw(collider, 'obstacle');
      }
      for (const item of collectibles.query(min, max)) this.colliders.draw(item.trackCollider, 'collectible');
      // Moving hazards: their current colliders (spared ones no longer collide and are not drawn).
      for (const hazard of this.game.hazards.query(min, max)) {
        hazard.trackColliders.forEach((collider, i) => {
          if (hazard.enabled[i]) this.colliders.draw(collider, 'hazard');
        });
      }
      this.drawTerrain(min, max);
    }
    this.colliders.end();
  }

  /** Segment terrain as boxes: surfaces (solid up to their top), pits (down to the floor), ceilings (underside slab, slabs up to their top). */
  private drawTerrain(min: number, max: number): void {
    const base = this.game.config.world.groundHeight;
    const wallHeight = this.game.config.player.collider.standingHeight;
    const b = this.box;
    for (const segment of this.game.track.segments) {
      const terrain = segment.definition.terrain;
      if (!terrain || segment.endDistance < min || segment.startDistance > max) continue;
      const zones = [
        ...terrain.surfaces.map((s) => ({ area: s, bottom: base, top: Math.max(s.heightFrom, s.heightTo) })),
        ...terrain.gaps.map((g) => ({ area: g, bottom: base - g.depth, top: base })),
        // slabs (walk-in pipe crowns) up to their walkable top, other ceilings as a thin slab / a wall of body height
        ...terrain.ceilings.map((c) => ({ area: c, bottom: c.clearance, top: c.top ?? c.clearance + (c.clearance - base < wallHeight / 2 ? wallHeight : TERRAIN_CEILING_SLAB) })),
      ];
      for (const { area, bottom, top } of zones) {
        b.minX = area.xFrom;
        b.maxX = area.xTo;
        b.minY = bottom;
        b.maxY = top;
        b.minZ = segment.startDistance + area.zFrom;
        b.maxZ = segment.startDistance + area.zTo;
        this.colliders.draw(b, 'terrain');
      }
    }
  }

  private collectStats(): Record<string, string | number | boolean | null> {
    const { game } = this;
    const { player, track, difficulty, pursuit, director, engine, scene } = game;
    const d = player.current.distance;
    const segment = track.segmentAt(d);
    const lanes = ['LEFT', 'CENTER', 'RIGHT'];
    return {
      FPS: Math.round(engine.getFps()),
      'Frame ms': this.instrumentation ? this.instrumentation.frameTimeCounter.lastSecAverage.toFixed(1) : null,
      'Draw Calls': this.instrumentation ? this.instrumentation.drawCallsCounter.current : null,
      'Aktive Meshes': scene.getActiveMeshes().length,
      Spielzustand: game.state.state,
      'Player State': player.state,
      Lane: `${player.currentLane} ${lanes[player.currentLane] ?? ''}`,
      'x / y': `${player.current.x.toFixed(2)} / ${player.current.y.toFixed(2)}`,
      'Speed m/s': player.speed.toFixed(2),
      Distanz: `${d.toFixed(1)} m`,
      Segment: segment ? segment.definition.id : null,
      'Segment #': segment ? `${segment.sequenceIndex} (uid ${segment.uid})` : null,
      'lokal z': segment ? segment.localZ(d).toFixed(2) : null,
      Segmente: `${track.segments.length} (+${track.previewSegments.length} Vorschau)`,
      Abzweig: this.turnInfo(),
      Kamera: this.cameraInfo(),
      Tier: difficulty.tierAt(d).id,
      Verfolger: `${pursuit.gap.toFixed(1)} m`,
      Gefahren: this.hazardInfo(),
      Unverwundbar: director.isInvulnerable || director.godMode,
      'Visuals (pool)': `${game.pool.stats.inUse} / ${game.pool.stats.created}`,
      'AD-Impressions': this.impressions,
      'Letzter Sound': game.audio.last,
      Seed: track.seed,
    };
  }

  /** Next moving hazards ahead: id, phase and distance (e.g. HAZ_FORKLIFT_L0 ACTIVE 23 m). */
  private hazardInfo(): string | null {
    const d = this.game.player.current.distance;
    const ahead = this.game.hazards
      .all()
      .filter((h) => h.segment.committed && h.bounds.maxZ > d)
      .sort((a, b) => a.bounds.minZ - b.bounds.minZ)
      .slice(0, 2);
    if (ahead.length === 0) return null;
    return ahead.map((h) => `${h.definition.id} ${h.model.phase}${h.spared ? ' (verschont)' : ''} ${Math.max(0, h.bounds.minZ - d).toFixed(0)} m`).join(' · ');
  }

  /** Camera yaw relative to the track, pitch and roll (degrees) – for diagnosing view problems. */
  private cameraInfo(): string {
    const { camera, track, player } = this.game;
    const c = camera.camera.rotation;
    const trackYaw = track.sampleFrame(player.current.distance).yaw;
    const deg = (r: number) => Math.round((r * 180) / Math.PI);
    const relative = deg(Math.atan2(Math.sin(c.y - trackYaw), Math.cos(c.y - trackYaw)));
    return `Δyaw ${relative}° · pitch ${deg(c.x)}° · roll ${deg(c.z)}°`;
  }

  private turnInfo(): string | null {
    const hint = this.game.turns.hint(this.game.player.current.distance);
    if (!hint) return null;
    return `${hint.options.join('/')} ${hint.decided ?? '–'} ${hint.distanceToPivot.toFixed(1)} m`;
  }

  private syncInstrumentation(enabled: boolean): void {
    if (enabled && !this.instrumentation) {
      this.instrumentation = new SceneInstrumentation(this.game.scene);
      this.instrumentation.captureFrameTime = true;
    } else if (!enabled && this.instrumentation) {
      this.instrumentation.dispose();
      this.instrumentation = null;
    }
  }
}
